/**
 * In-memory implementation of DataRepository used when Supabase is not configured.
 * It reproduces the same business rules as the database (GEM ID uniqueness,
 * derived status, amendment application, audit logging) so UI behaviour is identical.
 * Data resets on page reload.
 */
import type {
  AmendmentInput,
  AppSettings,
  AuditLog,
  Tender,
  TenderInput,
  TenderSummary,
  TenderDocument,
  UUID,
  VendorSummary,
  Vendor,
  QuantityTransaction,
  TenderLineItem,
  FinancialTransaction,
  Amendment,
  AlertDelivery,
  AlertEventType,
  TenderAlertEvent,
  NotificationSeverity,
  BudgetStatus,
  Department,
  MonthlyEstimate,
  ApprovingAuthority,
  BudgetHead,
  GlAccount,
} from '@/types/domain';
import { normalizeIndianMobile, normalizeIndianPhone, MOBILE_ERROR, PHONE_ERROR, EMAIL_ERROR, isValidEmail } from '@/lib/contact';
import {
  DomainError,
  type DataRepository,
  type Paged,
  type TenderQuery,
  type LedgerQuery,
  type TenderSortField,
  type LookupKind,
} from '../repository';
import { buildSeed, type SeedData } from './seed';
import { deriveTenderStatus, TENDER_STATUS_ORDER } from '@/lib/status';
import { addDays, daysUntil } from '@/lib/dates';
import { computeDashboard } from '../shared/aggregations';
import { billPeriodRuleError, grPoRuleError } from '@/lib/financials';
import { isBudgetDecision, isBudgetOpen } from '@/lib/orderBudget';
import { monthStart, monthsBetween, spreadEvenly } from '@/lib/estimates';
import { isClassificationMissing } from '@/lib/tenderClassification';

const LATENCY_MS = 120;
const delay = <T,>(value: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(structuredClone(value)), LATENCY_MS));

/** Minimal valid one-page PDF with two lines of text (demo previews only). */
function samplePdf(title: string, fileName: string): Blob {
  const clean = (t: string) => t.replace(/[()\\]/g, ' ').replace(/[^\x20-\x7E]/g, '-');
  const content = `BT /F1 20 Tf 72 720 Td (${clean(title)}) Tj 0 -32 Td /F1 12 Tf (Demo preview of ${clean(fileName)}) Tj 0 -20 Td (Real files are shown here once Supabase Storage is connected.) Tj ET`;
  const objs = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents 4 0 R /Resources << /Font << /F1 5 0 R >> >> >>',
    `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
  ];
  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [];
  objs.forEach((o, i) => {
    offsets.push(pdf.length);
    pdf += `${i + 1} 0 obj\n${o}\nendobj\n`;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objs.length + 1}\n0000000000 65535 f \n` + offsets.map((o) => `${String(o).padStart(10, '0')} 00000 n \n`).join('');
  pdf += `trailer\n<< /Size ${objs.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  return new Blob([pdf], { type: 'application/pdf' });
}

const LOOKUP_FK = { departments: 'departmentId', locations: 'locationId', approving_authorities: 'approvingAuthorityId', gl_accounts: 'glAccountId', budget_heads: 'budgetHeadId' } as const;
const LOOKUP_ONE = { departments: 'department', locations: 'location', approving_authorities: 'approving authority', gl_accounts: 'GL account', budget_heads: 'budget head' } as const;

function newId(): string {
  return crypto.randomUUID();
}

function paginate<T>(rows: T[], page = 1, pageSize = 25): Paged<T> {
  const start = (page - 1) * pageSize;
  return { rows: rows.slice(start, start + pageSize), total: rows.length, page, pageSize };
}

const norm = (s: string | null | undefined) => (s ?? '').toLowerCase();

export class DemoRepository implements DataRepository {
  readonly mode = 'demo' as const;
  private db: SeedData & { lineItems: TenderLineItem[] };
  private estimatesStore: MonthlyEstimate[] = [];
  /** Approving Committee authorities (same codes as migration 20261024120000_approving_authority.sql). */
  private authorities: ApprovingAuthority[] = [
    ['SBU', 'SBU Head'], ['HQ-OPS-HSSE', 'HQ Ops & HSSE I/c'], ['HQ-LOG', 'HQ Logistics I/c'], ['HQ-SALES', 'HQ Sales I/c'], ['HQ-FIN', 'HQ Finance I/c'],
  ].map(([code, name], i) => ({ id: `00000000-0000-4000-8012-${String(i).padStart(12, '0')}`, code, name, isActive: true, sortOrder: (i + 1) * 10 }));
  /** Sample GL accounts / budget heads (the real GL list comes from Finance). */
  private glAccountsStore: GlAccount[] = [
    ['5101010', 'Repairs & Maintenance'], ['5102020', 'Contract Services'], ['5103030', 'Transport & Handling'], ['5104040', 'Office Expenses'],
  ].map(([code, name], i) => ({ id: `00000000-0000-4000-8013-${String(i).padStart(12, '0')}`, code, name, isActive: true }));
  private budgetHeadsStore: BudgetHead[] = [
    ['OPEX-OPS', 'Operations (Opex)'], ['OPEX-MNT', 'Maintenance (Opex)'], ['CAPEX', 'Capital works'],
  ].map(([code, name], i) => ({ id: `00000000-0000-4000-8014-${String(i).padStart(12, '0')}`, code, name, isActive: true }));
  private settingsState: AppSettings = {
    organizationName: 'Your Organization',
    expiringThresholdDays: 60,
    billLagDays: 60,
    defaultCurrency: 'INR',
  };
  private currentUserId: UUID;

  constructor() {
    this.db = { lineItems: [], ...structuredClone(buildSeed()) } as SeedData & { lineItems: TenderLineItem[] };
    this.currentUserId = this.db.users[0].id;
    // Sample approvals: most awarded contracts have an approving authority (larger values go higher up).
    this.db.tenders.forEach((t, i) => {
      if (t.lifecycleStatus === 'draft' || i % 7 === 6 || !t.awardDate) return;
      const a = t.contractValue >= 1e7 ? this.authorities[0] : this.authorities[1 + (i % 4)];
      Object.assign(t, { approvingAuthorityId: a.id, approvalDate: addDays(t.awardDate, -((i % 9) + 3)), approvalReference: `NS/${t.awardDate.slice(0, 4)}/${String(100 + i).padStart(4, '0')}` });
    });
    // Sample Department Head: the first viewer becomes head of their department (shows the head features).
    const viewer = this.db.users.find((u) => u.role === 'viewer' && u.departmentId);
    const dep = viewer && this.db.departments.find((d) => d.id === viewer.departmentId);
    if (dep) dep.headUserId = viewer.id;
    // Sample budget heads / GL accounts on some tenders.
    this.db.tenders.forEach((t, i) => {
      if (i % 2 === 0) t.budgetHeadId = this.budgetHeadsStore[i % 3].id;
      if (i % 3 === 0) t.glAccountId = this.glAccountsStore[i % 4].id;
    });
    // Sample monthly estimates: every third active contract, contract value spread evenly over its months.
    this.db.tenders.forEach((t, i) => {
      if (i % 3 !== 0 || t.lifecycleStatus !== 'active' || !t.startDate || !t.endDate) return;
      spreadEvenly(t.contractValue, monthsBetween(t.startDate, t.endDate)).forEach((amount, m) => {
        this.estimatesStore.push({ id: newId(), tenderId: t.id, month: monthStart(m), estimatedAmount: amount, estimatedQuantity: null, remarks: null, createdAt: t.createdAt });
      });
    });
  }

  setCurrentUser(id: UUID) {
    this.currentUserId = id;
  }

  private get me() {
    return this.db.users.find((u) => u.id === this.currentUserId) ?? this.db.users[0];
  }

  // ------------------------------------------------------------ read model
  private summarize(t: Tender): TenderSummary {
    const vendor = this.db.vendors.find((v) => v.id === t.vendorId);
    const dept = this.db.departments.find((d) => d.id === t.departmentId);
    const loc = this.db.locations.find((l) => l.id === t.locationId);
    const qtx = this.db.quantity.filter((q) => q.tenderId === t.id);
    const consumed = qtx.reduce((s, q) => s + (q.txnType === 'reversal' ? -q.quantity : q.quantity), 0);
    const fin = this.db.financials.filter((f) => f.tenderId === t.id);
    const sum = (pred: (f: FinancialTransaction) => boolean) => fin.filter(pred).reduce((s, f) => s + f.amount, 0);
    return {
      ...t,
      vendorName: vendor?.name ?? null,
      vendorCode: vendor?.vendorCode ?? null,
      departmentName: dept?.name ?? null,
      locationName: loc?.name ?? null,
      consumedQuantity: consumed,
      remainingQuantity: t.totalQuantity - consumed,
      utilizationPct: t.totalQuantity > 0 ? Math.round((10000 * consumed) / t.totalQuantity) / 100 : 0,
      lastConsumptionDate: qtx.map((q) => q.txnDate).sort().at(-1) ?? null,
      invoicedAmount: sum((f) => f.txnType === 'invoice' && f.status !== 'rejected'),
      // Paid = bills (invoices) marked Paid, plus older separate Payment entries marked Paid.
      paidAmount: sum((f) => (f.txnType === 'invoice' || f.txnType === 'payment') && f.status === 'paid'),
      penaltyAmount: sum((f) => f.txnType === 'penalty' && f.status !== 'rejected'),
      pendingDocuments: this.db.documents.filter((d) => d.tenderId === t.id && d.status === 'pending').length,
      documentsToReview: this.db.documents.filter((d) => d.tenderId === t.id && d.status === 'uploaded').length,
      amendmentsPending: this.db.amendments.filter((a) => a.tenderId === t.id && a.status === 'draft').length,
      lineItemCount: this.db.lineItems.filter((li) => li.tenderId === t.id).length,
      previousGemId: this.db.tenders.find((x) => x.id === t.previousTenderId)?.gemId ?? null,
      renewedById: this.db.tenders.find((x) => x.previousTenderId === t.id)?.id ?? null,
      renewedByGemId: this.db.tenders.find((x) => x.previousTenderId === t.id)?.gemId ?? null,
      ownerName: this.db.users.find((u) => u.id === t.ownerId)?.fullName ?? null,
      ownerEmail: this.db.users.find((u) => u.id === t.ownerId)?.email ?? null,
      ownerPhone: this.db.users.find((u) => u.id === t.ownerId)?.phone ?? null,
      daysRemaining: daysUntil(t.endDate),
      status: deriveTenderStatus(t.lifecycleStatus, t.endDate, this.settingsState.expiringThresholdDays),
      detailsMissing: isClassificationMissing(t),
      orderType: t.orderType ?? 'original',
      repeatOfTenderId: t.repeatOfTenderId ?? null,
      repeatOfGemId: this.db.tenders.find((x) => x.id === t.repeatOfTenderId)?.gemId ?? null,
      repeatCount: this.db.tenders.filter((x) => x.repeatOfTenderId === t.id).length,
      budgetStatus: t.budgetStatus ?? 'none',
      approvingAuthorityName: this.authorities.find((a) => a.id === t.approvingAuthorityId)?.name ?? null,
      departmentHeadId: this.headOf(t.departmentId)?.id ?? null,
      departmentHeadName: this.headOf(t.departmentId)?.fullName ?? null,
      budgetHeadCode: this.budgetHeadsStore.find((b) => b.id === t.budgetHeadId)?.code ?? null,
      budgetHeadName: this.budgetHeadsStore.find((b) => b.id === t.budgetHeadId)?.name ?? null,
      glAccountCode: this.glAccountsStore.find((g) => g.id === t.glAccountId)?.code ?? null,
      glAccountName: this.glAccountsStore.find((g) => g.id === t.glAccountId)?.name ?? null,
      billProcessedTill: fin.filter((f) => f.txnType === 'invoice' && f.status === 'paid' && f.billPeriodTo).map((f) => f.billPeriodTo!).sort().at(-1) ?? null,
    };
  }

  /** Active head of a department (database: departments.head_user_id). */
  private headOf(departmentId: UUID | null | undefined) {
    const d = this.db.departments.find((x) => x.id === departmentId);
    const u = d?.headUserId ? this.db.users.find((x) => x.id === d.headUserId) : undefined;
    return u?.isActive ? u : undefined;
  }

  /** Mirrors the database rules tenders_repeat_link_check + guard_tender_repeat. */
  private assertRepeat(next: Partial<TenderInput>, existing: Tender | null) {
    const orderType = next.orderType ?? existing?.orderType ?? 'original';
    const repeatOf = 'repeatOfTenderId' in next ? next.repeatOfTenderId ?? null : existing?.repeatOfTenderId ?? null;
    if (orderType === 'original') {
      if (repeatOf) throw new DomainError('VALIDATION', 'Only a Repeat order can be linked to an original tender.');
      return;
    }
    if (!repeatOf) throw new DomainError('VALIDATION', 'Choose the original tender this repeat order belongs to.');
    if (existing && repeatOf === existing.id) throw new DomainError('VALIDATION', 'A tender cannot be a repeat order of itself.');
    const original = this.db.tenders.find((x) => x.id === repeatOf);
    if (!original || (original.orderType ?? 'original') !== 'original') {
      throw new DomainError('VALIDATION', 'A repeat order must be linked to an Original tender.');
    }
    if (existing && this.db.tenders.some((x) => x.repeatOfTenderId === existing.id)) {
      throw new DomainError('VALIDATION', 'This tender has repeat orders linked to it, so it must stay an Original order.');
    }
  }

  /** Mirrors the database rules tenders_budget_fields_check + guard_tender_budget. */
  private assertBudget(next: Partial<TenderInput>, existing: Tender | null) {
    const pick = <K extends keyof TenderInput>(k: K) => (k in next ? next[k] : existing?.[k]) ?? null;
    const status = (pick('budgetStatus') ?? 'none') as BudgetStatus;
    const amount = pick('budgetAmountSought') as number | null;
    const requestedOn = pick('budgetRequestedOn') as string | null;
    const decisionDate = pick('budgetDecisionDate') as string | null;
    if (amount != null && amount < 0) throw new DomainError('VALIDATION', 'Amount sought cannot be negative.');
    if (['requested', 'approved', 'rejected'].includes(status) && (amount == null || !requestedOn)) {
      throw new DomainError('VALIDATION', 'Enter the amount sought and the requested-on date.');
    }
    if (isBudgetDecision(status) && !decisionDate) throw new DomainError('VALIDATION', 'Enter the decision date.');
    if (decisionDate && requestedOn && decisionDate < requestedOn) throw new DomainError('VALIDATION', 'The decision date cannot be before the requested-on date.');
    const wasDecided = isBudgetDecision(existing?.budgetStatus);
    const changesDecision = !existing ||
      (['budgetStatus', 'budgetDecisionDate', 'budgetDecisionRemarks', 'budgetAmountSought'] as const)
        .some((k) => k in next && (next[k] ?? null) !== (existing[k] ?? null));
    const isHead = !!existing && this.headOf(existing.departmentId)?.id === this.me.id
      && (!('departmentId' in next) || next.departmentId === existing.departmentId);
    if ((isBudgetDecision(status) || wasDecided) && changesDecision && !['admin', 'manager'].includes(this.me.role) && !isHead) {
      throw new DomainError('FORBIDDEN', 'Only managers, administrators and the department head can approve or reject additional budget, or change a decision.');
    }
  }

  /** Mirrors the database trigger guard_tender_classification + unique index uq_tenders_crfq. */
  private assertClassification(next: Partial<TenderInput>, existing: Tender | null) {
    const crfq = next.tenderNumber === undefined ? existing?.tenderNumber ?? null : next.tenderNumber?.trim() || null;
    if (crfq && this.db.tenders.some((x) => x.id !== existing?.id && (x.tenderNumber ?? '').toUpperCase() === crfq.toUpperCase())) {
      throw new DomainError('DUPLICATE_CRFQ', `A tender with CRFQ Number "${crfq}" already exists.`);
    }
    const fields: [keyof TenderInput, string][] = [
      ['tenderNumber', 'CRFQ Number'], ['tenderingMethod', 'Tender Type'], ['tenderCategory', 'Tender Category'], ['floatedBy', 'Floated By'],
    ];
    for (const [k, label] of fields) {
      const value = k in next ? (k === 'tenderNumber' ? crfq : next[k]) : existing?.[k];
      if (!existing && !value) throw new DomainError('VALIDATION', `${label} is required.`);
      if (existing && existing[k] && !value) throw new DomainError('VALIDATION', `${label} cannot be removed once entered.`);
    }
  }

  private allSummaries(): TenderSummary[] {
    return this.db.tenders.map((t) => this.summarize(t));
  }

  private logAudit(entry: Omit<AuditLog, 'id' | 'occurredAt' | 'actorId' | 'actorEmail' | 'actorName'>) {
    const me = this.me;
    this.db.audit.unshift({
      id: (this.db.audit[0]?.id ?? 0) + 1,
      occurredAt: new Date().toISOString(),
      actorId: me.id,
      actorEmail: me.email,
      actorName: me.fullName,
      ...entry,
    });
  }

  private assertCanWrite() {
    if (!['admin', 'manager', 'editor'].includes(this.me.role)) {
      throw new DomainError('FORBIDDEN', 'Your role does not permit this action.');
    }
  }

  // ------------------------------------------------------------ owner alerts (mirrors the database)
  private alertEvents: TenderAlertEvent[] = [];

  private assertOwnerChange(t: Pick<Tender, 'ownerId'> | null, nextOwner: UUID | null | undefined) {
    if (nextOwner === undefined) return;
    const changing = t ? (t.ownerId ?? null) !== (nextOwner ?? null) : nextOwner !== null && nextOwner !== this.me.id;
    if (!changing) return;
    if (!['admin', 'manager'].includes(this.me.role)) {
      throw new DomainError('FORBIDDEN', 'Only managers and administrators can change the tender owner.');
    }
    const u = this.db.users.find((x) => x.id === nextOwner);
    if (nextOwner && (!u || !u.isActive || !['admin', 'manager', 'editor'].includes(u.role))) {
      throw new DomainError('VALIDATION', 'The tender owner must be an active administrator, manager or editor.');
    }
  }

  private emitAlert(t: Tender, type: AlertEventType, key: string, severity: NotificationSeverity, headline: string, suppressed = false): boolean {
    if (this.alertEvents.some((e) => e.tenderId === t.id && (e as TenderAlertEvent & { key?: string }).key === key)) return false;
    const s = this.summarize(t);
    const owner = this.db.users.find((u) => u.id === t.ownerId);
    const fmt = (n: number) => n.toLocaleString('en-IN', { maximumFractionDigits: 3 });
    const end = t.endDate ? t.endDate.split('-').reverse().join('/') : '—';
    const message = [
      headline,
      `Tender: ${t.gemId}`,
      `Vendor: ${s.vendorName ?? '—'}`,
      `Consumption: ${Math.round(s.utilizationPct * 10) / 10}%`,
      `Consumed: ${fmt(s.consumedQuantity)} / ${fmt(t.totalQuantity)} ${t.unitOfMeasure}`,
      `Remaining: ${fmt(s.remainingQuantity)} ${t.unitOfMeasure}`,
      `Contract End Date: ${end}`,
      `Tender Owner: ${owner?.fullName ?? 'Not assigned'}`,
      `Department: ${s.departmentName ?? '—'}${s.departmentHeadName ? ` (head: ${s.departmentHeadName})` : ''}`,
    ].join('\n');
    const now = new Date().toISOString();
    const deliveries: AlertDelivery[] = [];
    const add = (d: Omit<AlertDelivery, 'id' | 'attempts' | 'updatedAt'>) => deliveries.push({ id: newId(), attempts: 0, updatedAt: now, ...d });
    if (!suppressed) {
      if (!owner || !owner.isActive) {
        add({ channel: 'in_app', status: 'skipped', error: owner ? 'Tender owner is deactivated — assign a new owner.' : 'Tender has no owner — assign one on the tender.' });
      } else {
        if (type.startsWith('consumption')) {
          add({ channel: 'in_app', status: 'sent', destination: owner.email, sentAt: now });
          this.db.notifications.unshift({
            id: newId(), userId: owner.id, tenderId: t.id, kind: 'consumption', severity, title: `${headline} — ${t.gemId}`,
            body: `${s.vendorName ? `${s.vendorName} · ` : ''}${fmt(s.consumedQuantity)} of ${fmt(t.totalQuantity)} ${t.unitOfMeasure} consumed, ${fmt(s.remainingQuantity)} remaining`,
            link: `/tenders/${t.id}?tab=quantity`, readAt: null, createdAt: now,
          });
        }
      }
      if (owner?.isActive && type.startsWith('budget') ) {
        this.db.notifications.unshift({
          id: newId(), userId: owner.id, tenderId: t.id, kind: 'budget', severity, title: `${headline} — ${t.gemId}`,
          body: s.vendorName ?? null, link: `/tenders/${t.id}`, readAt: null, createdAt: now,
        });
      }
      // Department Head: in-app for every alert type (once, also when the head owns the tender).
      const head = this.headOf(t.departmentId);
      if (head && head.id !== owner?.id) {
        this.db.notifications.unshift({
          id: newId(), userId: head.id, tenderId: t.id, kind: type.startsWith('consumption') ? 'consumption' : type.startsWith('budget') ? 'budget' : 'expiry', severity,
          title: `${headline} — ${t.gemId}`, body: `${s.departmentName ?? ''} department${s.vendorName ? ` · ${s.vendorName}` : ''}`, link: `/tenders/${t.id}`, readAt: null, createdAt: now,
        });
      }
      // Email: owner + department head + every active administrator and manager (one each).
      const emailTo = this.db.users.filter((u) => u.isActive && u.email && ((owner?.isActive && u.id === owner.id) || u.id === head?.id || u.role === 'admin' || u.role === 'manager'));
      for (const u of emailTo) add({ channel: 'email', status: 'not_configured', destination: u.email, provider: 'brevo', error: 'Email cannot be sent in demo mode.' });
    }
    this.alertEvents.unshift({
      id: newId(), tenderId: t.id, gemId: t.gemId, tenderTitle: t.title, ownerId: t.ownerId ?? null, ownerName: owner?.fullName ?? null,
      eventType: type, severity, title: `${headline} — ${t.gemId}`, message, suppressed, createdAt: now, deliveries,
      ...({ key } as object),
    } as TenderAlertEvent);
    return true;
  }

  /** Database: trg_budget_alert — one alert each time the additional-budget status changes (not to None). */
  private budgetAlert(t: Tender, before: BudgetStatus) {
    const now = t.budgetStatus ?? 'none';
    if (now === before || now === 'none') return;
    const label = { needed: 'Needed', requested: 'Requested', approved: 'Approved', rejected: 'Rejected' }[now];
    this.emitAlert(t, `budget_${now}` as AlertEventType, `budget_${now}:${new Date().toISOString()}`, now === 'rejected' ? 'critical' : now === 'approved' ? 'info' : 'warning', `Additional Budget ${label}`);
  }

  private checkConsumption(t: Tender): number {
    if (t.lifecycleStatus !== 'active' || t.totalQuantity <= 0) return 0;
    const pct = (100 * this.summarize(t).consumedQuantity) / t.totalQuantity;
    const top = [50, 75, 90, 100].filter((b) => b <= pct).at(-1);
    if (!top) return 0;
    let n = 0;
    for (const b of [50, 75, 90, 100].filter((x) => x <= top)) {
      const headline = b === 100 ? 'Contract Quantity Exhausted (100%)' : `Contract Consumption Alert (${b}%)`;
      if (this.emitAlert(t, `consumption_${b}` as AlertEventType, this.hasLineItems(t.id) ? `consumption_${b}:li` : `consumption_${b}:q${t.totalQuantity}`, b >= 90 ? 'critical' : b >= 75 ? 'warning' : 'info', headline, b < top)) n++;
    }
    return n;
  }

  private checkExpiry(): number {
    let n = 0;
    for (const t of this.db.tenders) {
      if (t.lifecycleStatus !== 'active' || !t.endDate) continue;
      const days = daysUntil(t.endDate) ?? 999;
      if (days > 30) continue;
      if (days < 0) {
        if (this.emitAlert(t, 'expired', `expired:${t.endDate}`, 'critical', `Contract Expired on ${t.endDate.split('-').reverse().join('/')}`)) n++;
      } else {
        const b = days <= 7 ? 7 : 30;
        if (this.emitAlert(t, `expiry_${b}` as AlertEventType, `expiry_${b}:${t.endDate}`, b === 7 ? 'critical' : 'warning', `Contract Expiring in ${days} day${days === 1 ? '' : 's'}`)) n++;
      }
    }
    return n;
  }

  private ownerAlertsSeeded = false;
  /** First use: raise alerts for the sample data, like the database migration does. */
  private seedOwnerAlerts() {
    if (this.ownerAlertsSeeded) return;
    this.ownerAlertsSeeded = true;
    this.db.tenders.forEach((t) => this.checkConsumption(t));
    this.checkExpiry();
  }

  // ------------------------------------------------------------ tenders
  tenders: DataRepository['tenders'] = {
    list: async (q: TenderQuery) => {
      let rows = this.allSummaries();
      const term = norm(q.search).trim();
      if (term) {
        const f = q.searchField ?? 'all';
        rows = rows.filter((t) => {
          const fields: Record<string, string> = {
            gemId: norm(t.gemId),
            tenderNumber: norm(t.tenderNumber),
            vendor: norm(t.vendorName) + ' ' + norm(t.vendorCode),
            poNumber: norm(t.poNumber),
            contractNumber: norm(t.contractNumber),
          };
          return f === 'all'
            ? Object.values(fields).some((v) => v.includes(term)) || norm(t.title).includes(term)
            : fields[f]?.includes(term);
        });
      }
      if (q.statuses?.length) rows = rows.filter((t) => q.statuses!.includes(t.status));
      if (q.departmentId) rows = rows.filter((t) => t.departmentId === q.departmentId);
      if (q.locationId) rows = rows.filter((t) => t.locationId === q.locationId);
      if (q.vendorId) rows = rows.filter((t) => t.vendorId === q.vendorId);
      if (q.startFrom) rows = rows.filter((t) => !!t.startDate && t.startDate >= q.startFrom!);
      if (q.startTo) rows = rows.filter((t) => !!t.startDate && t.startDate <= q.startTo!);
      if (q.endFrom) rows = rows.filter((t) => !!t.endDate && t.endDate >= q.endFrom!);
      if (q.endTo) rows = rows.filter((t) => !!t.endDate && t.endDate <= q.endTo!);
      if (q.valueMin != null) rows = rows.filter((t) => t.contractValue >= q.valueMin!);
      if (q.valueMax != null) rows = rows.filter((t) => t.contractValue <= q.valueMax!);
      if (q.utilizationMin != null) rows = rows.filter((t) => t.utilizationPct >= q.utilizationMin!);
      if (q.utilizationMax != null) rows = rows.filter((t) => t.utilizationPct <= q.utilizationMax!);
      if (q.tenderingMethod) rows = rows.filter((t) => t.tenderingMethod === q.tenderingMethod);
      if (q.tenderCategory) rows = rows.filter((t) => t.tenderCategory === q.tenderCategory);
      if (q.floatedBy) rows = rows.filter((t) => t.floatedBy === q.floatedBy);
      if (q.detailsMissing) rows = rows.filter((t) => t.detailsMissing);
      if (q.orderType) rows = rows.filter((t) => (t.orderType ?? 'original') === q.orderType);
      if (q.repeatOfTenderId) rows = rows.filter((t) => t.repeatOfTenderId === q.repeatOfTenderId);
      if (q.budget === 'open') rows = rows.filter((t) => isBudgetOpen(t.budgetStatus));
      else if (q.budget) rows = rows.filter((t) => (t.budgetStatus ?? 'none') === q.budget);
      if (q.approvingAuthorityId === 'none') rows = rows.filter((t) => !t.approvingAuthorityId);
      else if (q.approvingAuthorityId) rows = rows.filter((t) => t.approvingAuthorityId === q.approvingAuthorityId);
      if (q.budgetHeadId === 'none') rows = rows.filter((t) => !t.budgetHeadId);
      else if (q.budgetHeadId) rows = rows.filter((t) => t.budgetHeadId === q.budgetHeadId);

      const sort = q.sort ?? { field: 'updatedAt' as TenderSortField, direction: 'desc' as const };
      const dir = sort.direction === 'asc' ? 1 : -1;
      rows.sort((a, b) => {
        const av = sort.field === 'status' ? TENDER_STATUS_ORDER.indexOf(a.status) : (a[sort.field] as unknown);
        const bv = sort.field === 'status' ? TENDER_STATUS_ORDER.indexOf(b.status) : (b[sort.field] as unknown);
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return (av as number | string) > (bv as number | string) ? dir : (av as number | string) < (bv as number | string) ? -dir : 0;
      });
      return delay(paginate(rows, q.page, q.pageSize));
    },

    getById: async (id) => {
      const t = this.db.tenders.find((x) => x.id === id);
      return delay(t ? this.summarize(t) : null);
    },

    getByGemId: async (gemId) => {
      const t = this.db.tenders.find((x) => x.gemId.toUpperCase() === gemId.trim().toUpperCase());
      return delay(t ? this.summarize(t) : null);
    },

    isGemIdAvailable: async (gemId, excludeId) =>
      delay(!this.db.tenders.some((t) => t.gemId.toUpperCase() === gemId.trim().toUpperCase() && t.id !== excludeId)),

    isCrfqAvailable: async (crfq, excludeId) =>
      delay(!this.db.tenders.some((t) => (t.tenderNumber ?? '').toUpperCase() === crfq.trim().toUpperCase() && t.id !== excludeId)),

    create: async (input: TenderInput) => {
      this.assertCanWrite();
      const gemId = input.gemId.trim();
      if (this.db.tenders.some((t) => t.gemId.toUpperCase() === gemId.toUpperCase())) {
        throw new DomainError('DUPLICATE_GEM_ID', `A tender with GEM ID "${gemId}" already exists.`);
      }
      this.assertClassification(input, null);
      this.assertRepeat(input, null);
      this.assertBudget(input, null);
      this.assertOwnerChange(null, input.ownerId ?? null);
      const now = new Date().toISOString();
      const t: Tender = { ...input, tenderNumber: input.tenderNumber?.trim() || null, ownerId: input.ownerId ?? this.me.id, gemId, id: newId(), createdAt: now, updatedAt: now, closedAt: null };
      this.db.tenders.unshift(t);
      this.logAudit({ action: 'insert', entityType: 'tenders', entityId: t.id, tenderId: t.id, gemId, changes: { gem_id: gemId, title: t.title }, summary: `Created tender ${gemId}` });
      return delay(this.summarize(t));
    },

    update: async (id, patch) => {
      this.assertCanWrite();
      const t = this.db.tenders.find((x) => x.id === id);
      if (!t) throw new DomainError('NOT_FOUND', 'Tender not found.');
      if (patch.gemId && patch.gemId.trim().toUpperCase() !== t.gemId.toUpperCase()) {
        if (this.db.tenders.some((x) => x.id !== id && x.gemId.toUpperCase() === patch.gemId!.trim().toUpperCase())) {
          throw new DomainError('DUPLICATE_GEM_ID', `A tender with GEM ID "${patch.gemId}" already exists.`);
        }
      }
      this.assertClassification(patch, t);
      this.assertRepeat(patch, t);
      this.assertBudget(patch, t);
      if (patch.tenderNumber !== undefined) patch = { ...patch, tenderNumber: patch.tenderNumber?.trim() || null };
      this.assertOwnerChange(t, patch.ownerId);
      const changes: Record<string, unknown> = {};
      (Object.keys(patch) as (keyof TenderInput)[]).forEach((k) => {
        if (patch[k] !== t[k]) changes[k] = { from: t[k], to: patch[k] };
      });
      const budgetBefore = t.budgetStatus ?? 'none';
      Object.assign(t, patch, { updatedAt: new Date().toISOString() });
      this.syncTenderValues(t); // values of line-item tenders come from their line items
      if (Object.keys(changes).length) {
        this.logAudit({ action: 'update', entityType: 'tenders', entityId: id, tenderId: id, gemId: t.gemId, changes, summary: `Updated ${Object.keys(changes).join(', ')}` });
      }
      this.budgetAlert(t, budgetBefore);
      this.seedOwnerAlerts();
      this.checkConsumption(t);
      return delay(this.summarize(t));
    },
    remove: async (id) => {
      if (this.me.role !== 'admin') throw new DomainError('FORBIDDEN', 'Only administrators can delete tenders.');
      const t = this.db.tenders.find((x) => x.id === id);
      if (!t) throw new DomainError('NOT_FOUND', 'Tender not found.');
      if (this.db.tenders.some((x) => x.repeatOfTenderId === id)) {
        throw new DomainError('IN_USE', 'This tender has repeat orders linked to it. Delete or relink them first.');
      }
      this.db.tenders = this.db.tenders.filter((x) => x.id !== id);
      this.db.tenders.forEach((x) => { if (x.previousTenderId === id) x.previousTenderId = null; });
      this.db.quantity = this.db.quantity.filter((x) => x.tenderId !== id);
      this.db.financials = this.db.financials.filter((x) => x.tenderId !== id);
      this.db.documents = this.db.documents.filter((x) => x.tenderId !== id);
      this.db.amendments = this.db.amendments.filter((x) => x.tenderId !== id);
      this.estimatesStore = this.estimatesStore.filter((x) => x.tenderId !== id);
      this.db.notifications = this.db.notifications.filter((x) => x.tenderId !== id);
      this.alertEvents = this.alertEvents.filter((x) => x.tenderId !== id);
      this.logAudit({ action: 'delete', entityType: 'tenders', entityId: id, tenderId: null, gemId: t.gemId, changes: { gem_id: t.gemId, title: t.title }, summary: `Deleted tender ${t.gemId}` });
      return delay(undefined);
    },
    decideBudget: async (id, input) => {
      const t = this.db.tenders.find((x) => x.id === id);
      if (!t) throw new DomainError('NOT_FOUND', 'Tender not found.');
      if (!['admin', 'manager'].includes(this.me.role) && this.headOf(t.departmentId)?.id !== this.me.id) {
        throw new DomainError('FORBIDDEN', 'Only managers, administrators and the department head can approve or reject additional budget.');
      }
      if (!['requested', 'approved', 'rejected'].includes(t.budgetStatus ?? 'none')) {
        throw new DomainError('VALIDATION', 'Only a Requested additional budget can be approved or rejected.');
      }
      if (t.budgetRequestedOn && input.decisionDate < t.budgetRequestedOn) throw new DomainError('VALIDATION', 'The decision date cannot be before the requested-on date.');
      const before = t.budgetStatus ?? 'none';
      Object.assign(t, { budgetStatus: input.status, budgetDecisionDate: input.decisionDate, budgetDecisionRemarks: input.remarks?.trim() || null, updatedAt: new Date().toISOString() });
      this.logAudit({ action: 'update', entityType: 'tenders', entityId: id, tenderId: id, gemId: t.gemId, changes: { budget_status: { from: before, to: input.status } }, summary: `Additional budget ${input.status}` });
      this.budgetAlert(t, before);
      return delay(this.summarize(t));
    },
    close: async (id, input) => {
      if (!['admin', 'manager'].includes(this.me.role)) throw new DomainError('FORBIDDEN', 'Only managers and administrators can close contracts.');
      const t = this.db.tenders.find((x) => x.id === id);
      if (!t) throw new DomainError('NOT_FOUND', 'Tender not found.');
      const from = t.lifecycleStatus;
      t.lifecycleStatus = input.outcome;
      t.closedAt = new Date(`${input.closedOn}T12:00:00`).toISOString();
      t.closureRemarks = input.remarks.trim();
      t.updatedAt = new Date().toISOString();
      this.logAudit({ action: 'update', entityType: 'tenders', entityId: id, tenderId: id, gemId: t.gemId, changes: { lifecycle_status: { from, to: input.outcome } }, summary: `Contract ${input.outcome}` });
      return delay(this.summarize(t));
    },
  };

  // ------------------------------------------------------------ vendors
  private vendorSummary(v: Vendor): VendorSummary {
    const ts = this.allSummaries().filter((t) => t.vendorId === v.id);
    return {
      ...v,
      tenderCount: ts.length,
      activeCount: ts.filter((t) => t.status === 'active' || t.status === 'expiring_soon').length,
      totalContractValue: ts.reduce((s, t) => s + t.contractValue, 0),
      avgUtilization: ts.length ? ts.reduce((s, t) => s + t.utilizationPct, 0) / ts.length : 0,
    };
  }

  /** Same rules as the database: technical contact must be valid; the vendor contact phone is normalised when valid. */
  private checkVendorContacts<T extends Partial<Vendor>>(v: T): T {
    const out = { ...v };
    if (out.technicalContactPhone) {
      const p = normalizeIndianPhone(out.technicalContactPhone);
      if (!p) throw new DomainError('VALIDATION', PHONE_ERROR);
      out.technicalContactPhone = p;
    }
    if (out.technicalContactEmail && !isValidEmail(out.technicalContactEmail)) throw new DomainError('VALIDATION', EMAIL_ERROR);
    if (out.phone && normalizeIndianPhone(out.phone)) out.phone = normalizeIndianPhone(out.phone);
    return out;
  }

  vendors: DataRepository['vendors'] = {
    list: async () => delay(this.db.vendors.map((v) => this.vendorSummary(v)).sort((a, b) => a.name.localeCompare(b.name))),
    getById: async (id) => {
      const v = this.db.vendors.find((x) => x.id === id);
      return delay(v ? this.vendorSummary(v) : null);
    },
    create: async (input) => {
      this.assertCanWrite();
      if (this.db.vendors.some((v) => v.vendorCode.toUpperCase() === input.vendorCode.toUpperCase())) {
        throw new DomainError('VALIDATION', `Vendor code "${input.vendorCode}" already exists.`);
      }
      const v: Vendor = { ...this.checkVendorContacts(input), id: newId(), createdAt: new Date().toISOString() };
      this.db.vendors.push(v);
      this.logAudit({ action: 'insert', entityType: 'vendors', entityId: v.id, tenderId: null, changes: { name: v.name }, summary: `Added vendor ${v.name}` });
      return delay(v);
    },
    update: async (id, patch) => {
      this.assertCanWrite();
      const v = this.db.vendors.find((x) => x.id === id);
      if (!v) throw new DomainError('NOT_FOUND', 'Vendor not found.');
      if (patch.vendorCode && this.db.vendors.some((x) => x.id !== id && x.vendorCode.toUpperCase() === patch.vendorCode!.toUpperCase())) {
        throw new DomainError('VALIDATION', `Vendor code "${patch.vendorCode}" already exists.`);
      }
      Object.assign(v, this.checkVendorContacts(patch));
      this.logAudit({ action: 'update', entityType: 'vendors', entityId: id, tenderId: null, changes: patch as Record<string, unknown>, summary: `Updated vendor ${v.name}` });
      return delay(v);
    },
    remove: async (id) => {
      this.assertCanWrite();
      if (this.db.tenders.some((t) => t.vendorId === id)) {
        throw new DomainError('IN_USE', 'This vendor is linked to tenders and cannot be deleted. Mark it inactive instead.');
      }
      const v = this.db.vendors.find((x) => x.id === id);
      this.db.vendors = this.db.vendors.filter((x) => x.id !== id);
      this.logAudit({ action: 'delete', entityType: 'vendors', entityId: id, tenderId: null, changes: { name: v?.name }, summary: `Deleted vendor ${v?.name ?? ''}` });
      return delay(undefined);
    },
  };

  lookups: DataRepository['lookups'] = {
    departments: async () => delay(this.db.departments.filter((d) => d.isActive)),
    locations: async () => delay(this.db.locations.filter((l) => l.isActive)),
    approvingAuthorities: async () => delay(this.authorities.filter((a) => a.isActive).sort((a, b) => (a.sortOrder ?? 100) - (b.sortOrder ?? 100))),
    glAccounts: async () => delay(this.glAccountsStore.filter((g) => g.isActive).sort((a, b) => a.code.localeCompare(b.code))),
    budgetHeads: async () => delay(this.budgetHeadsStore.filter((b) => b.isActive).sort((a, b) => a.code.localeCompare(b.code))),
    listAll: async (kind) => {
      const fk = LOOKUP_FK[kind];
      const list = this.lookupList(kind);
      return delay(
        [...list]
          .sort((a, b) => kind === 'approving_authorities' ? ((a as ApprovingAuthority).sortOrder ?? 100) - ((b as ApprovingAuthority).sortOrder ?? 100)
            : kind === 'gl_accounts' || kind === 'budget_heads' ? a.code.localeCompare(b.code) : a.name.localeCompare(b.name))
          .map((r) => ({
            id: r.id, code: r.code, name: r.name, isActive: r.isActive,
            state: kind === 'locations' ? (r.state ?? null) : undefined,
            headUserId: kind === 'departments' ? ((r as Department).headUserId ?? null) : undefined,
            headName: kind === 'departments' ? (this.db.users.find((u) => u.id === (r as Department).headUserId)?.fullName ?? null) : undefined,
            usageCount: this.db.tenders.filter((t) => t[fk] === r.id).length
              + (kind === 'gl_accounts' ? this.db.lineItems.filter((li) => li.glAccountId === r.id).length : 0),
          })),
      );
    },
    save: async (kind, row) => {
      if (!['admin', 'manager'].includes(this.me.role)) throw new DomainError('FORBIDDEN', 'Your role does not permit this action.');
      const list = this.lookupList(kind);
      const code = row.code.trim().toUpperCase();
      if (list.some((r) => r.code.toUpperCase() === code && r.id !== row.id)) {
        throw new DomainError('VALIDATION', 'This code is already used. Codes must be unique.');
      }
      if (kind === 'departments' && row.headUserId) {
        const u = this.db.users.find((x) => x.id === row.headUserId);
        if (!u?.isActive) throw new DomainError('VALIDATION', 'The department head must be an active TCMS user.');
      }
      const next = {
        code, name: row.name.trim(), isActive: row.isActive,
        ...(kind === 'locations' ? { state: row.state?.trim() || null } : {}),
        ...(kind === 'departments' && row.headUserId !== undefined ? { headUserId: row.headUserId || null } : {}),
      };
      let saved;
      if (row.id) {
        saved = list.find((r) => r.id === row.id);
        if (!saved) throw new DomainError('NOT_FOUND', 'Record not found.');
        Object.assign(saved, next);
      } else {
        saved = { id: newId(), ...next, ...(kind === 'approving_authorities' ? { sortOrder: 100 } : {}) };
        list.push(saved);
      }
      this.logAudit({ action: row.id ? 'update' : 'insert', entityType: kind, entityId: saved.id, tenderId: null, changes: { code, name: next.name }, summary: `${row.id ? 'Updated' : 'Added'} ${LOOKUP_ONE[kind]} ${next.name}` });
      return delay({ ...saved, state: saved.state ?? null, usageCount: 0 });
    },
    remove: async (kind, id) => {
      if (!['admin', 'manager'].includes(this.me.role)) throw new DomainError('FORBIDDEN', 'Your role does not permit this action.');
      const fk = LOOKUP_FK[kind];
      if (this.db.tenders.some((t) => t[fk] === id) || (kind === 'gl_accounts' && this.db.lineItems.some((li) => li.glAccountId === id))) {
        throw new DomainError('IN_USE', 'This record is still used by other records and cannot be deleted. Deactivate it instead.');
      }
      if (kind === 'departments') this.db.departments = this.db.departments.filter((d) => d.id !== id);
      else if (kind === 'locations') this.db.locations = this.db.locations.filter((l) => l.id !== id);
      else if (kind === 'gl_accounts') this.glAccountsStore = this.glAccountsStore.filter((g) => g.id !== id);
      else if (kind === 'budget_heads') this.budgetHeadsStore = this.budgetHeadsStore.filter((b) => b.id !== id);
      else this.authorities = this.authorities.filter((a) => a.id !== id);
      return delay(undefined);
    },
  };

  private lookupList(kind: LookupKind): { id: string; code: string; name: string; state?: string | null; isActive: boolean }[] {
    return kind === 'departments' ? this.db.departments : kind === 'locations' ? this.db.locations
      : kind === 'gl_accounts' ? this.glAccountsStore : kind === 'budget_heads' ? this.budgetHeadsStore : this.authorities;
  }

  // ------------------------------------------------------------ ledgers
  private withTender<T extends { tenderId: UUID }>(row: T) {
    const t = this.db.tenders.find((x) => x.id === row.tenderId)!;
    const vendor = this.db.vendors.find((v) => v.id === t.vendorId);
    return { ...row, gemId: t.gemId, tenderTitle: t.title, unitOfMeasure: t.unitOfMeasure, vendorName: vendor?.name ?? null };
  }

  private filterLedger<T extends { tenderId: UUID; txnDate?: string; createdAt: string }>(rows: T[], q: LedgerQuery) {
    let out = rows;
    if (q.tenderId) out = out.filter((r) => r.tenderId === q.tenderId);
    if (q.from) out = out.filter((r) => (r.txnDate ?? r.createdAt) >= q.from!);
    if (q.to) out = out.filter((r) => (r.txnDate ?? r.createdAt).slice(0, 10) <= q.to!);
    return out;
  }

  private matchesSearch(row: { gemId: string; tenderTitle: string; referenceNo?: string | null }, term?: string) {
    const s = norm(term).trim();
    if (!s) return true;
    return norm(row.gemId).includes(s) || norm(row.tenderTitle).includes(s) || norm(row.referenceNo).includes(s);
  }

  quantity: DataRepository['quantity'] = {
    listByTender: async (tenderId) =>
      delay(this.db.quantity.filter((q) => q.tenderId === tenderId).sort((a, b) => (a.txnDate < b.txnDate ? 1 : -1))),
    ledger: async (q) => {
      const rows = this.filterLedger(this.db.quantity, q)
        .map((r) => this.withTender(r))
        .filter((r) => this.matchesSearch(r, q.search))
        .sort((a, b) => (a.txnDate < b.txnDate ? 1 : -1));
      return delay(paginate(rows, q.page, q.pageSize));
    },
    create: async (input) => {
      this.assertCanWrite();
      const t = this.db.tenders.find((x) => x.id === input.tenderId);
      if (!t) throw new DomainError('NOT_FOUND', 'Tender not found.');
      input = { ...input, ...this.applyLineItem(t, input.lineItemId ?? null, input.itemQuantity ?? null, input.txnType, null, input.quantity) };
      const s = this.summarize(t);
      if (!this.hasLineItems(t.id) && input.txnType === 'consumption' && input.quantity > s.remainingQuantity) {
        throw new DomainError('VALIDATION', `Quantity exceeds remaining balance (${s.remainingQuantity} ${t.unitOfMeasure}).`);
      }
      const row: QuantityTransaction = { ...input, id: newId(), createdAt: new Date().toISOString(), createdBy: this.me.id, createdByName: this.me.fullName };
      this.db.quantity.push(row);
      this.logAudit({ action: 'insert', entityType: 'quantity_transactions', entityId: row.id, tenderId: t.id, gemId: t.gemId, changes: { quantity: row.quantity, txn_type: row.txnType }, summary: `Recorded ${row.txnType} of ${row.quantity} ${t.unitOfMeasure}` });
      this.seedOwnerAlerts();
      this.checkConsumption(t);
      return delay(row);
    },
    update: async (id, patch) => {
      if (!['admin', 'manager', 'editor'].includes(this.me.role)) throw new DomainError('FORBIDDEN', 'Your role cannot edit consumption records.');
      const row = this.db.quantity.find((q) => q.id === id);
      if (!row) throw new DomainError('NOT_FOUND', 'This record no longer exists.');
      const t = this.db.tenders.find((x) => x.id === row.tenderId)!;
      patch = { ...patch, ...this.applyLineItem(t, patch.lineItemId ?? null, patch.itemQuantity ?? null, patch.txnType, row, patch.quantity) };
      if (!(patch.quantity > 0)) throw new DomainError('VALIDATION', 'Quantity must be greater than zero.');
      const signed = (type: QuantityTransaction['txnType'], q: number) => (type === 'reversal' ? -q : q);
      const before = this.summarize(t).consumedQuantity;
      const after = before - signed(row.txnType, row.quantity) + signed(patch.txnType, patch.quantity);
      this.assertQuantityBalance(t, before, after);
      const changes: Record<string, unknown> = {};
      if (row.quantity !== patch.quantity) changes.quantity = { from: row.quantity, to: patch.quantity };
      if (row.txnType !== patch.txnType) changes.txn_type = { from: row.txnType, to: patch.txnType };
      if (row.txnDate !== patch.txnDate) changes.txn_date = { from: row.txnDate, to: patch.txnDate };
      Object.assign(row, { ...patch, referenceNo: patch.referenceNo ?? null, remarks: patch.remarks ?? null });
      this.logAudit({ action: 'update', entityType: 'quantity_transactions', entityId: row.id, tenderId: t.id, gemId: t.gemId, changes, summary: `Edited ${row.txnType} record (${row.quantity} ${t.unitOfMeasure})` });
      this.checkConsumption(t);
      return delay({ ...row });
    },
    remove: async (id) => {
      if (!['admin', 'manager'].includes(this.me.role)) throw new DomainError('FORBIDDEN', 'Only managers and administrators can delete consumption records.');
      const row = this.db.quantity.find((q) => q.id === id);
      if (!row) throw new DomainError('NOT_FOUND', 'This record no longer exists.');
      const t = this.db.tenders.find((x) => x.id === row.tenderId)!;
      const before = this.summarize(t).consumedQuantity;
      this.assertQuantityBalance(t, before, before - (row.txnType === 'reversal' ? -row.quantity : row.quantity));
      if (row.lineItemId && this.itemUsed(row.lineItemId, row.id) < 0) {
        throw new DomainError('VALIDATION', 'Not allowed: this would make the used quantity of the line item negative. Remove or correct the reversal first.');
      }
      this.db.quantity = this.db.quantity.filter((q) => q.id !== id);
      this.logAudit({ action: 'delete', entityType: 'quantity_transactions', entityId: id, tenderId: t.id, gemId: t.gemId, changes: { quantity: row.quantity, txn_type: row.txnType }, summary: `Deleted ${row.txnType} record (${row.quantity} ${t.unitOfMeasure})` });
      return delay(undefined);
    },
  };

  // ------------------------------------------------------------ line items (mirror of the database rules)
  private hasLineItems(tenderId: UUID) {
    return this.db.lineItems.some((li) => li.tenderId === tenderId);
  }

  private itemUsed(itemId: UUID, excludeId?: UUID) {
    return this.db.quantity
      .filter((q) => q.lineItemId === itemId && q.id !== excludeId)
      .reduce((s, q) => s + (q.txnType === 'reversal' ? -1 : 1) * (q.itemQuantity ?? 0), 0);
  }

  /** Line-item tenders: contract value = Σ amounts; "quantity" basis = value in ₹. */
  private syncTenderValues(t: Tender) {
    if (!this.hasLineItems(t.id)) return;
    const sum = Math.round(this.db.lineItems.filter((li) => li.tenderId === t.id).reduce((s, li) => s + li.amount, 0) * 100) / 100;
    t.contractValue = sum; t.totalQuantity = sum; t.unitOfMeasure = 'INR';
  }

  /** Validates a usage record against its line item and returns the fields to store. */
  private applyLineItem(t: Tender, lineItemId: UUID | null, itemQuantity: number | null, txnType: QuantityTransaction['txnType'], existing: QuantityTransaction | null, quantity: number) {
    if (!lineItemId) {
      if (this.hasLineItems(t.id)) throw new DomainError('VALIDATION', 'This tender has line items — choose the line item this usage belongs to.');
      return { lineItemId: null, itemQuantity: null, quantity };
    }
    const li = this.db.lineItems.find((x) => x.id === lineItemId);
    if (!li || li.tenderId !== t.id) throw new DomainError('VALIDATION', 'The selected line item does not belong to this tender.');
    if (!(itemQuantity != null && itemQuantity > 0)) throw new DomainError('VALIDATION', 'Enter a quantity greater than zero.');
    const others = this.itemUsed(li.id, existing?.id);
    const before = others + (existing && existing.lineItemId === li.id ? (existing.txnType === 'reversal' ? -1 : 1) * (existing.itemQuantity ?? 0) : 0);
    const after = others + (txnType === 'reversal' ? -itemQuantity : itemQuantity);
    if (after < 0) throw new DomainError('VALIDATION', `Reversal is more than what was used on "${li.description}" (${others} ${li.unit}).`);
    if (after > li.quantity && after > before) throw new DomainError('VALIDATION', `Exceeds the ordered quantity of "${li.description}": ${li.quantity} ${li.unit} ordered, ${others} ${li.unit} already used.`);
    return { lineItemId: li.id, itemQuantity, quantity: Math.max(Math.round(itemQuantity * li.unitRate * 1000) / 1000, 0.001) };
  }

  private toLineItem(li: TenderLineItem): TenderLineItem {
    const usage = this.db.quantity.filter((q) => q.lineItemId === li.id);
    return { ...li, usedQuantity: Math.round(this.itemUsed(li.id) * 1000) / 1000, usageCount: usage.length };
  }

  estimates: DataRepository['estimates'] = {
    listByTender: async (tenderId) =>
      delay(this.estimatesStore.filter((e) => e.tenderId === tenderId).sort((a, b) => (a.month < b.month ? -1 : 1)).map((e) => ({ ...e }))),
    listRange: async (from, to) => delay(this.estimatesStore.filter((e) => e.month >= from && e.month <= to).map((e) => ({ ...e }))),
    save: async (tenderId, rows) => {
      this.assertCanWrite();
      const t = this.db.tenders.find((x) => x.id === tenderId);
      if (!t) throw new DomainError('NOT_FOUND', 'Tender not found.');
      const now = new Date().toISOString();
      let changed = 0;
      rows.forEach((r) => {
        if (!/^\d{4}-\d{2}-01$/.test(r.month)) throw new DomainError('VALIDATION', `Month must be the 1st of a month (${r.month}).`);
        if (r.estimatedAmount != null && r.estimatedAmount < 0) throw new DomainError('VALIDATION', 'Estimated amount cannot be negative.');
        if (r.estimatedQuantity != null && r.estimatedQuantity < 0) throw new DomainError('VALIDATION', 'Estimated quantity cannot be negative.');
        const i = this.estimatesStore.findIndex((e) => e.tenderId === tenderId && e.month === r.month);
        if (r.estimatedAmount == null) {
          if (i >= 0) { this.estimatesStore.splice(i, 1); changed++; }
          return;
        }
        const next = { estimatedAmount: r.estimatedAmount, estimatedQuantity: r.estimatedQuantity ?? null, remarks: r.remarks?.trim() || null };
        if (i >= 0) {
          const e = this.estimatesStore[i];
          if (e.estimatedAmount !== next.estimatedAmount || (e.estimatedQuantity ?? null) !== next.estimatedQuantity || (e.remarks ?? null) !== next.remarks) {
            Object.assign(e, next, { updatedAt: now });
            changed++;
          }
        } else {
          this.estimatesStore.push({ id: newId(), tenderId, month: r.month, ...next, createdAt: now });
          changed++;
        }
      });
      if (changed) {
        this.logAudit({ action: 'update', entityType: 'tender_monthly_estimates', entityId: tenderId, tenderId, gemId: t.gemId, changes: { months: changed }, summary: `Updated monthly estimates (${changed} month${changed === 1 ? '' : 's'})` });
      }
      return this.estimates.listByTender(tenderId);
    },
  };

  lineItems: DataRepository['lineItems'] = {
    listByTender: async (tenderId) =>
      delay(this.db.lineItems.filter((li) => li.tenderId === tenderId).sort((a, b) => a.lineNo - b.lineNo).map((li) => this.toLineItem(li))),
    create: async (input, opts?: { converting?: boolean }) => {
      this.assertCanWrite();
      const t = this.db.tenders.find((x) => x.id === input.tenderId);
      if (!t) throw new DomainError('NOT_FOUND', 'Tender not found.');
      this.validateLineItem(input);
      if (!opts?.converting && !this.hasLineItems(t.id) && this.db.quantity.some((q) => q.tenderId === t.id && !q.lineItemId)) {
        throw new DomainError('VALIDATION', 'This tender already has consumption recorded without line items. Use "Convert to line items" first, so the existing records are kept and linked.');
      }
      const lineNo = Math.max(0, ...this.db.lineItems.filter((li) => li.tenderId === t.id).map((li) => li.lineNo)) + 1;
      const row: TenderLineItem = {
        id: newId(), tenderId: t.id, lineNo, description: input.description.trim(), quantity: input.quantity, unit: input.unit.trim(),
        unitRate: input.unitRate, amount: Math.round(input.quantity * input.unitRate * 100) / 100, remarks: input.remarks?.trim() || null,
        glAccountId: input.glAccountId ?? null, createdAt: new Date().toISOString(), usedQuantity: 0, usageCount: 0,
      };
      this.db.lineItems.push(row);
      this.syncTenderValues(t);
      this.logAudit({ action: 'insert', entityType: 'tender_line_items', entityId: row.id, tenderId: t.id, gemId: t.gemId, changes: { description: row.description, quantity: row.quantity, unit: row.unit, unit_rate: row.unitRate }, summary: `Added line item "${row.description}" (${row.quantity} ${row.unit} × ₹${row.unitRate})` });
      this.checkConsumption(t);
      return delay(this.toLineItem(row));
    },
    update: async (id, patch) => {
      this.assertCanWrite();
      const li = this.db.lineItems.find((x) => x.id === id);
      if (!li) throw new DomainError('NOT_FOUND', 'This line item no longer exists.');
      this.validateLineItem(patch);
      const used = this.itemUsed(li.id);
      if (patch.quantity < used) throw new DomainError('VALIDATION', `Quantity cannot be less than what is already used (${used} ${patch.unit}).`);
      const t = this.db.tenders.find((x) => x.id === li.tenderId)!;
      const rateChanged = patch.unitRate !== li.unitRate;
      Object.assign(li, { description: patch.description.trim(), quantity: patch.quantity, unit: patch.unit.trim(), unitRate: patch.unitRate, remarks: patch.remarks?.trim() || null, amount: Math.round(patch.quantity * patch.unitRate * 100) / 100, updatedAt: new Date().toISOString(),
        ...(patch.glAccountId !== undefined ? { glAccountId: patch.glAccountId || null } : {}) });
      this.syncTenderValues(t);
      if (rateChanged) this.db.quantity.filter((q) => q.lineItemId === li.id).forEach((q) => { q.quantity = Math.max(Math.round((q.itemQuantity ?? 0) * li.unitRate * 1000) / 1000, 0.001); });
      this.logAudit({ action: 'update', entityType: 'tender_line_items', entityId: li.id, tenderId: t.id, gemId: t.gemId, changes: { quantity: li.quantity, unit_rate: li.unitRate }, summary: `Edited line item "${li.description}"` });
      this.checkConsumption(t);
      return delay(this.toLineItem(li));
    },
    remove: async (id) => {
      if (!['admin', 'manager'].includes(this.me.role)) throw new DomainError('FORBIDDEN', 'Only managers and administrators can delete line items.');
      const li = this.db.lineItems.find((x) => x.id === id);
      if (!li) throw new DomainError('NOT_FOUND', 'This line item no longer exists.');
      if (this.db.quantity.some((q) => q.lineItemId === id)) throw new DomainError('VALIDATION', 'This line item has usage recorded, so it cannot be deleted. Delete or correct its usage entries first.');
      const t = this.db.tenders.find((x) => x.id === li.tenderId)!;
      this.db.lineItems = this.db.lineItems.filter((x) => x.id !== id);
      this.syncTenderValues(t);
      this.logAudit({ action: 'delete', entityType: 'tender_line_items', entityId: id, tenderId: t.id, gemId: t.gemId, changes: { description: li.description, amount: li.amount }, summary: `Deleted line item "${li.description}"` });
      return delay(undefined);
    },
    convertTender: async (tenderId, description) => {
      this.assertCanWrite();
      const t = this.db.tenders.find((x) => x.id === tenderId);
      if (!t) throw new DomainError('NOT_FOUND', 'Tender not found.');
      if (this.hasLineItems(t.id)) throw new DomainError('VALIDATION', 'This tender already uses line items.');
      if (!(t.totalQuantity > 0) || !(t.contractValue > 0)) throw new DomainError('VALIDATION', "Set the tender's quantity and contract value first (both must be more than zero).");
      // alerts already sent must not be re-sent under the new key
      for (const e of this.alertEvents.filter((x) => x.tenderId === t.id && x.eventType.startsWith('consumption'))) {
        const key = `${String((e as unknown as { key?: string }).key ?? e.eventType).split(':')[0]}:li`;
        if (!this.alertEvents.some((x) => x.tenderId === t.id && (x as unknown as { key?: string }).key === key)) {
          this.alertEvents.push({ ...e, id: newId(), suppressed: true, deliveries: [], ...({ key } as object) } as TenderAlertEvent);
        }
      }
      const rate = Math.round((t.contractValue / t.totalQuantity) * 1e6) / 1e6;
      const unit = t.unitOfMeasure;
      const createItem = this.lineItems.create as (i: Parameters<DataRepository['lineItems']['create']>[0], o: { converting: boolean }) => Promise<TenderLineItem>;
      const item = await createItem({ tenderId: t.id, description: description?.trim() || t.title, quantity: t.totalQuantity, unit, unitRate: rate }, { converting: true });
      this.db.quantity.filter((q) => q.tenderId === t.id && !q.lineItemId).forEach((q) => {
        q.lineItemId = item.id; q.itemQuantity = q.quantity; q.quantity = Math.max(Math.round(q.quantity * rate * 1000) / 1000, 0.001);
      });
      this.syncTenderValues(t);
      return delay(undefined);
    },
  };

  private validateLineItem(i: { description: string; quantity: number; unit: string; unitRate: number }) {
    if (!i.description?.trim()) throw new DomainError('VALIDATION', 'Enter the service / item description.');
    if (!(i.quantity > 0)) throw new DomainError('VALIDATION', 'Quantity must be greater than zero.');
    if (!i.unit?.trim()) throw new DomainError('VALIDATION', 'Choose a unit.');
    if (!(i.unitRate > 0)) throw new DomainError('VALIDATION', 'Unit rate must be greater than zero.');
  }

  /** Same rule as the database guard: consumed quantity may not go below zero or above the contract. */
  private assertQuantityBalance(t: Tender, before: number, after: number) {
    if (after < 0) throw new DomainError('VALIDATION', `Not allowed: this would make the consumed quantity negative (${after} ${t.unitOfMeasure}). Remove or correct the reversal first.`);
    if (after > t.totalQuantity && after > before) throw new DomainError('VALIDATION', `Not allowed: consumed quantity would be ${after} ${t.unitOfMeasure}, more than the contracted ${t.totalQuantity} ${t.unitOfMeasure}.`);
  }

  financials: DataRepository['financials'] = {
    listByTender: async (tenderId) =>
      delay(this.db.financials.filter((f) => f.tenderId === tenderId).sort((a, b) => (a.txnDate < b.txnDate ? 1 : -1))),
    ledger: async (q) => {
      let rows = this.filterLedger(this.db.financials, q).map((r) => this.withTender(r));
      if (q.txnType) rows = rows.filter((r) => r.txnType === q.txnType);
      rows = rows.filter((r) => this.matchesSearch(r, q.search)).sort((a, b) => (a.txnDate < b.txnDate ? 1 : -1));
      return delay(paginate(rows, q.page, q.pageSize));
    },
    create: async (input) => {
      this.assertCanWrite();
      const t = this.db.tenders.find((x) => x.id === input.tenderId);
      if (!t) throw new DomainError('NOT_FOUND', 'Tender not found.');
      const grErr = grPoRuleError(input) ?? billPeriodRuleError(input);
      if (grErr) throw new DomainError('VALIDATION', grErr);
      const grNum = input.grPoNumber?.trim() || null;
      const row: FinancialTransaction = { ...input, grPoType: grNum ? input.grPoType : null, grPoNumber: grNum, id: newId(), createdAt: new Date().toISOString() };
      this.db.financials.push(row);
      this.logAudit({ action: 'insert', entityType: 'financial_transactions', entityId: row.id, tenderId: t.id, gemId: t.gemId, changes: { txn_type: row.txnType, amount: row.amount }, summary: `Recorded ${row.txnType.replace('_', ' ')} of ₹${row.amount.toLocaleString('en-IN')}` });
      return delay(row);
    },
    update: async (id, patch) => {
      this.assertCanWrite();
      const row = this.db.financials.find((f) => f.id === id);
      if (!row) throw new DomainError('NOT_FOUND', 'This entry no longer exists.');
      const grNum = patch.grPoNumber?.trim() || null;
      const grErr = grPoRuleError(patch, row) ?? billPeriodRuleError({ ...row, ...patch }, row);
      if (grErr) throw new DomainError('VALIDATION', grErr);
      const t = this.db.tenders.find((x) => x.id === row.tenderId)!;
      const changes: Record<string, unknown> = {};
      (['txnType', 'status', 'amount', 'taxAmount', 'txnDate', 'grPoType', 'grPoNumber', 'billPeriodFrom', 'billPeriodTo'] as const).forEach((k) => { if ((patch as Record<string, unknown>)[k] !== row[k]) changes[k] = { from: row[k], to: (patch as Record<string, unknown>)[k] }; });
      Object.assign(row, patch, { grPoType: grNum ? patch.grPoType : null, grPoNumber: grNum });
      this.logAudit({ action: 'update', entityType: 'financial_transactions', entityId: id, tenderId: t.id, gemId: t.gemId, changes, summary: `Edited ${row.txnType.replace('_', ' ')} of ₹${row.amount.toLocaleString('en-IN')}` });
      return delay({ ...row });
    },
    remove: async (id) => {
      if (!['admin', 'manager'].includes(this.me.role)) throw new DomainError('FORBIDDEN', 'Only managers and administrators can delete financial transactions.');
      const row = this.db.financials.find((f) => f.id === id);
      if (!row) throw new DomainError('NOT_FOUND', 'This entry no longer exists.');
      // Paid entries can be deleted too (admin/manager); every deletion is kept in the audit log.
      const t = this.db.tenders.find((x) => x.id === row.tenderId)!;
      this.db.financials = this.db.financials.filter((f) => f.id !== id);
      this.logAudit({ action: 'delete', entityType: 'financial_transactions', entityId: id, tenderId: t.id, gemId: t.gemId, changes: { txn_type: row.txnType, amount: row.amount }, summary: `Deleted ${row.txnType.replace('_', ' ')} of ₹${row.amount.toLocaleString('en-IN')}` });
      return delay(undefined);
    },
  };

  documents: DataRepository['documents'] = {
    listByTender: async (tenderId) => delay(this.db.documents.filter((d) => d.tenderId === tenderId)),
    list: async (q) => {
      let rows = this.db.documents.map((d) => this.withTender(d));
      if (q.tenderId) rows = rows.filter((d) => d.tenderId === q.tenderId);
      if (q.status === 'received') rows = rows.filter((d) => d.status === 'uploaded' || d.status === 'verified');
      else if (q.status) rows = rows.filter((d) => d.status === q.status);
      if (q.docType) rows = rows.filter((d) => d.docType === q.docType);
      const s = norm(q.search).trim();
      if (s) rows = rows.filter((d) => norm(d.gemId).includes(s) || norm(d.title).includes(s) || norm(d.fileName).includes(s) || norm(d.tenderTitle).includes(s));
      rows.sort((a, b) => ((a.uploadedAt ?? a.dueDate ?? '') < (b.uploadedAt ?? b.dueDate ?? '') ? 1 : -1));
      return delay(paginate(rows, q.page, q.pageSize));
    },
    upload: async (tenderId, file, meta) => {
      this.assertCanWrite();
      const t = this.db.tenders.find((x) => x.id === tenderId);
      if (!t) throw new DomainError('NOT_FOUND', 'Tender not found.');
      const now = new Date().toISOString();
      const existing = meta.documentId ? this.db.documents.find((d) => d.id === meta.documentId) : undefined;
      const id = existing?.id ?? newId();
      const doc: TenderDocument = {
        ...(existing ?? { createdAt: now, version: 0, dueDate: null, remarks: null, verifiedAt: null }),
        id,
        tenderId,
        docType: meta.docType,
        title: meta.title,
        status: 'uploaded',
        storagePath: `${tenderId}/${id}/${file.name}`,
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        version: (existing?.version ?? 0) + 1,
        uploadedByName: this.me.fullName,
        uploadedAt: now,
        reviewRemarks: null,
        verifiedAt: null,
      } as TenderDocument;
      if (existing) Object.assign(existing, doc);
      else this.db.documents.push(doc);
      this.logAudit({ action: existing ? 'update' : 'insert', entityType: 'documents', entityId: id, tenderId, gemId: t.gemId, changes: { file_name: file.name, doc_type: meta.docType }, summary: `Uploaded ${meta.title}` });
      return delay(doc);
    },
    review: async (id, decision, remarks) => {
      if (!['admin', 'manager'].includes(this.me.role)) throw new DomainError('FORBIDDEN', 'Only managers and administrators can verify documents.');
      const d = this.db.documents.find((x) => x.id === id);
      if (!d) throw new DomainError('NOT_FOUND', 'Document not found.');
      d.status = decision;
      d.reviewRemarks = remarks?.trim() || null;
      d.verifiedAt = new Date().toISOString();
      const t = this.db.tenders.find((x) => x.id === d.tenderId);
      this.logAudit({ action: 'update', entityType: 'documents', entityId: id, tenderId: d.tenderId, gemId: t?.gemId, changes: { status: decision }, summary: `Document ${decision}: ${d.title}` });
      return delay(undefined);
    },
    getDownloadUrl: async () => delay(null), // no binaries in demo mode
    // Demo: generate a one-page placeholder PDF so the viewer can be tried without real storage.
    getFileBlob: async (doc) => delay(doc.fileName ? samplePdf(doc.title, doc.fileName) : null),
  };

  amendments: DataRepository['amendments'] = {
    listByTender: async (tenderId) =>
      delay(this.db.amendments.filter((a) => a.tenderId === tenderId).sort((a, b) => b.amendmentNo - a.amendmentNo)),
    create: async (input: AmendmentInput) => {
      this.assertCanWrite();
      // Mirrors RLS policy amendments_insert: only managers and administrators amend.
      if (!['admin', 'manager'].includes(this.me.role)) throw new DomainError('FORBIDDEN', 'Only managers and administrators can amend contracts.');
      const t = this.db.tenders.find((x) => x.id === input.tenderId);
      if (!t) throw new DomainError('NOT_FOUND', 'Tender not found.');
      const no = Math.max(0, ...this.db.amendments.filter((a) => a.tenderId === t.id).map((a) => a.amendmentNo)) + 1;
      const am: Amendment = {
        ...input,
        id: newId(),
        amendmentNo: no,
        previousValue: input.newValue != null ? t.contractValue : null,
        previousQuantity: input.newQuantity != null ? t.totalQuantity : null,
        previousEndDate: input.newEndDate != null ? t.endDate : null,
        approvedAt: input.status === 'approved' ? new Date().toISOString() : null,
        createdAt: new Date().toISOString(),
      };
      this.db.amendments.push(am);
      // Mirror DB trigger apply_approved_amendment()
      if (am.status === 'approved') {
        if (am.newValue != null) t.contractValue = am.newValue;
        if (am.newQuantity != null) t.totalQuantity = am.newQuantity;
        if (am.newEndDate != null) t.endDate = am.newEndDate;
        this.syncTenderValues(t);
        t.updatedAt = new Date().toISOString();
        this.seedOwnerAlerts();
        this.checkConsumption(t);
      }
      this.logAudit({ action: 'insert', entityType: 'amendments', entityId: am.id, tenderId: t.id, gemId: t.gemId, changes: { amendment_type: am.amendmentType, status: am.status }, summary: `Amendment #${no} (${am.amendmentType.replace('_', ' ')}) ${am.status}` });
      return delay(am);
    },
    listPending: async () =>
      delay(
        this.db.amendments
          .filter((a) => a.status === 'draft')
          .map((a) => this.withTender(a))
          .sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1)),
      ),
    review: async (id, decision, remarks) => {
      if (!['admin', 'manager'].includes(this.me.role)) throw new DomainError('FORBIDDEN', 'Only managers and administrators can approve amendments.');
      const am = this.db.amendments.find((a) => a.id === id);
      if (!am) throw new DomainError('NOT_FOUND', 'Amendment not found.');
      if (am.status !== 'draft') throw new DomainError('VALIDATION', `This amendment has already been ${am.status}.`);
      const t = this.db.tenders.find((x) => x.id === am.tenderId)!;
      am.status = decision;
      am.reviewRemarks = remarks?.trim() || null;
      if (decision === 'approved') {
        am.approvedAt = new Date().toISOString();
        if (am.newValue != null) t.contractValue = am.newValue;
        if (am.newQuantity != null) t.totalQuantity = am.newQuantity;
        if (am.newEndDate != null) t.endDate = am.newEndDate;
        this.syncTenderValues(t);
        t.updatedAt = new Date().toISOString();
        this.seedOwnerAlerts();
        this.checkConsumption(t);
      }
      this.logAudit({ action: 'update', entityType: 'amendments', entityId: id, tenderId: t.id, gemId: t.gemId, changes: { status: { from: 'draft', to: decision } }, summary: `Amendment #${am.amendmentNo} ${decision}` });
      return delay(undefined);
    },
  };

  audit: DataRepository['audit'] = {
      list: async (q) => {
        let rows = this.db.audit;
        if (q.tenderId) rows = rows.filter((a) => a.tenderId === q.tenderId);
        if (q.entityType) rows = rows.filter((a) => a.entityType === q.entityType);
        if (q.action) rows = rows.filter((a) => a.action === q.action);
        if (q.from) rows = rows.filter((a) => a.occurredAt.slice(0, 10) >= q.from!);
        if (q.to) rows = rows.filter((a) => a.occurredAt.slice(0, 10) <= q.to!);
        const s = norm(q.search).trim();
        if (s) rows = rows.filter((a) => norm(a.gemId).includes(s) || norm(a.summary).includes(s) || norm(a.actorName).includes(s) || norm(a.actorEmail).includes(s));
        return delay(paginate(rows, q.page, q.pageSize));
      },
  };

  notifications: DataRepository['notifications'] = {
    listMine: async () => {
      this.seedOwnerAlerts();
      return delay(this.db.notifications.filter((n) => n.userId === this.me.id));
    },
    markRead: async (id) => {
      const n = this.db.notifications.find((x) => x.id === id);
      if (n && !n.readAt) n.readAt = new Date().toISOString();
      return delay(undefined);
    },
    markAllRead: async () => {
      const now = new Date().toISOString();
      this.db.notifications.forEach((n) => (n.readAt ??= now));
      return delay(undefined);
    },
    runChecks: async () => {
      if (!['admin', 'manager'].includes(this.me.role)) throw new DomainError('FORBIDDEN', 'Only administrators and managers can run alert checks.');
      this.seedOwnerAlerts();
      let n = 0;
      this.db.tenders.forEach((t) => (n += this.checkConsumption(t)));
      n += this.checkExpiry();
      return delay({ created: 0, ownerAlerts: n, emailed: 0, emailError: null });
    },
  };

  email: DataRepository['email'] = {
    keyConfigured: async () => delay(false),
    sendTest: async () => {
      throw new DomainError('VALIDATION', 'Email sending is not available in demo mode.');
    },
    result: async () => delay({ pending: true }),
  };

  users: DataRepository['users'] = {
    list: async () => delay(this.db.users),
    updateRole: async (id, role) => {
      if (this.me.role !== 'admin') throw new DomainError('FORBIDDEN', 'Only administrators can change roles.');
      const u = this.db.users.find((x) => x.id === id);
      if (u) {
        this.logAudit({ action: 'update', entityType: 'profiles', entityId: id, tenderId: null, changes: { role: { from: u.role, to: role } }, summary: `Changed role of ${u.fullName} to ${role}` });
        u.role = role;
      }
      return delay(undefined);
    },
    setActive: async (id, active) => {
      if (this.me.role !== 'admin') throw new DomainError('FORBIDDEN', 'Only administrators can change user status.');
      const u = this.db.users.find((x) => x.id === id);
      if (u) {
        u.isActive = active;
        this.logAudit({ action: 'update', entityType: 'profiles', entityId: id, tenderId: null, changes: { is_active: active }, summary: `${active ? 'Activated' : 'Deactivated'} ${u.fullName}` });
      }
      return delay(undefined);
    },
    updateContact: async (id, patch) => {
      if (this.me.role !== 'admin' && this.me.id !== id) throw new DomainError('FORBIDDEN', 'Only administrators can change other users’ details.');
      const u = this.db.users.find((x) => x.id === id);
      if (!u) throw new DomainError('NOT_FOUND', 'User not found.');
      if (patch.phone) {
        const phone = normalizeIndianMobile(patch.phone);
        if (!phone) throw new DomainError('VALIDATION', MOBILE_ERROR);
        patch = { ...patch, phone };
      }
      const changes: Record<string, unknown> = {};
      if (patch.fullName !== undefined && patch.fullName !== u.fullName) changes.full_name = { from: u.fullName, to: patch.fullName };
      if (patch.designation !== undefined && patch.designation !== u.designation) changes.designation = { from: u.designation, to: patch.designation };
      if (patch.phone !== undefined && patch.phone !== (u.phone ?? null)) changes.phone = { from: u.phone ?? null, to: patch.phone };
      Object.assign(u, patch);
      if (Object.keys(changes).length) this.logAudit({ action: 'update', entityType: 'profiles', entityId: id, tenderId: null, changes, summary: `Updated contact details of ${u.fullName}` });
      return delay(undefined);
    },
    create: async (input) => {
      if (this.me.role !== 'admin') throw new DomainError('FORBIDDEN', 'Only administrators can manage users');
      const email = input.email.trim().toLowerCase();
      if (!isValidEmail(email)) throw new DomainError('VALIDATION', EMAIL_ERROR);
      if (this.db.users.some((x) => x.email.toLowerCase() === email)) throw new DomainError('VALIDATION', 'A user with this email already exists.');
      if (input.password.length < 8 || !/[A-Za-z]/.test(input.password) || !/\d/.test(input.password)) {
        throw new DomainError('VALIDATION', 'Password must be at least 8 characters with a letter and a number.');
      }
      const phone = input.phone ? normalizeIndianMobile(input.phone) : null;
      if (input.phone && !phone) throw new DomainError('VALIDATION', MOBILE_ERROR);
      const dept = this.db.departments.find((d) => d.id === input.departmentId);
      const u = {
        id: newId(), email, fullName: input.fullName.trim(), role: input.role, phone, designation: input.designation || null,
        departmentId: dept?.id ?? null, departmentName: dept?.name ?? null, isActive: true,
        mustChangePassword: input.mustChangePassword, lastLoginAt: null, createdAt: new Date().toISOString(),
      };
      this.db.users.push(u);
      this.logAudit({ action: 'insert', entityType: 'profiles', entityId: u.id, tenderId: null, changes: { email, role: input.role }, summary: `Added user ${u.fullName}` });
      return delay(u.id);
    },
    resetPassword: async (id, password, mustChangePassword) => {
      if (this.me.role !== 'admin') throw new DomainError('FORBIDDEN', 'Only administrators can manage users');
      if (id === this.me.id) throw new DomainError('VALIDATION', 'Change your own password under My Account.');
      if (password.length < 8 || !/[A-Za-z]/.test(password) || !/\d/.test(password)) {
        throw new DomainError('VALIDATION', 'Password must be at least 8 characters with a letter and a number.');
      }
      const u = this.db.users.find((x) => x.id === id);
      if (!u) throw new DomainError('NOT_FOUND', 'User not found.');
      u.mustChangePassword = mustChangePassword;
      this.logAudit({ action: 'update', entityType: 'profiles', entityId: id, tenderId: null, changes: { password: 'reset by administrator' }, summary: `Password reset for ${u.email}` });
      return delay(undefined);
    },
    ownedTenderCount: async (id) => delay(this.db.tenders.filter((t) => t.ownerId === id).length),
    remove: async (id, transferTo) => {
      if (this.me.role !== 'admin' || !this.me.canDeleteUsers) throw new DomainError('FORBIDDEN', 'You do not have permission to delete users');
      if (id === this.me.id) throw new DomainError('VALIDATION', 'You cannot delete your own account.');
      const u = this.db.users.find((x) => x.id === id);
      if (!u) throw new DomainError('NOT_FOUND', 'User not found.');
      const owned = this.db.tenders.filter((t) => t.ownerId === id);
      if (owned.length) {
        const to = this.db.users.find((x) => x.id === transferTo);
        if (!to) throw new DomainError('VALIDATION', `${u.fullName} owns ${owned.length} tender(s). Choose who takes them over before deleting.`);
        if (to.id === id || !to.isActive || !['admin', 'manager', 'editor'].includes(to.role)) {
          throw new DomainError('VALIDATION', 'Tenders can only be handed to an active administrator, manager or editor.');
        }
        owned.forEach((t) => (t.ownerId = to.id));
      }
      this.db.users = this.db.users.filter((x) => x.id !== id);
      this.db.notifications = this.db.notifications.filter((n) => n.userId !== id);
      this.logAudit({ action: 'delete', entityType: 'profiles', entityId: id, tenderId: null, changes: { email: u.email, tenders_transferred: owned.length }, summary: `Deleted user ${u.email}` });
      return delay({ tendersTransferred: owned.length });
    },
  };

  alerts: DataRepository['alerts'] = {
    list: async (q) => {
      this.seedOwnerAlerts();
      const oversight = ['admin', 'manager', 'auditor'].includes(this.me.role);
      let rows = this.alertEvents.filter((e) => oversight || e.ownerId === this.me.id);
      if (q.tenderId) rows = rows.filter((e) => e.tenderId === q.tenderId);
      if (q.status) rows = rows.filter((e) => e.deliveries.some((d) => d.status === q.status && (!q.channel || d.channel === q.channel)));
      else if (q.channel) rows = rows.filter((e) => e.deliveries.some((d) => d.channel === q.channel));
      const term = norm(q.search).trim();
      if (term) rows = rows.filter((e) => norm(e.gemId).includes(term));
      return delay(paginate(rows.map((e) => { const { key: _k, ...rest } = e as TenderAlertEvent & { key?: string }; void _k; return rest; }), q.page, q.pageSize));
    },
    retry: async () => {
      if (!['admin', 'manager'].includes(this.me.role)) throw new DomainError('FORBIDDEN', 'Only managers and administrators can retry alerts.');
      throw new DomainError('VALIDATION', 'Alerts cannot be sent in demo mode — connect Supabase and an email provider first.');
    },
    textChannels: async () => delay({ sms: { provider: 'none', ready: false }, whatsapp: { provider: 'none', ready: false } }),
  };

  analytics: DataRepository['analytics'] = {
    consumptionDataset: async () =>
      delay({
        tenders: this.allSummaries(),
        transactions: this.db.quantity.map((q) => ({ tenderId: q.tenderId, txnDate: q.txnDate, txnType: q.txnType, quantity: q.quantity })),
      }),
  };

  settings: DataRepository['settings'] = {
    get: async () => delay(this.settingsState),
    update: async (patch) => {
      if (this.me.role !== 'admin') throw new DomainError('FORBIDDEN', 'Only administrators can change settings.');
      this.settingsState = { ...this.settingsState, ...patch };
      return delay(this.settingsState);
    },
  };

  dashboard: DataRepository['dashboard'] = {
    summary: async () => {
      const pending = this.db.documents
        .filter((d) => d.status === 'pending')
        .map((d) => this.withTender(d))
        .sort((a, b) => ((a.dueDate ?? '') > (b.dueDate ?? '') ? 1 : -1))
        .slice(0, 8);
      return delay(computeDashboard(this.allSummaries(), pending, this.db.audit.slice(0, 10)));
    },
  };

  backup: DataRepository['backup'] = {
    exportAll: async () =>
      delay([
        { name: 'Tender summary', rows: this.allSummaries() as unknown as Record<string, unknown>[] },
        { name: 'vendors', rows: this.db.vendors as unknown as Record<string, unknown>[] },
        { name: 'departments', rows: this.db.departments as unknown as Record<string, unknown>[] },
        { name: 'locations', rows: this.db.locations as unknown as Record<string, unknown>[] },
        { name: 'tender_line_items', rows: this.db.lineItems as unknown as Record<string, unknown>[] },
        { name: 'tender_monthly_estimates', rows: this.estimatesStore as unknown as Record<string, unknown>[] },
        { name: 'quantity_transactions', rows: this.db.quantity as unknown as Record<string, unknown>[] },
        { name: 'financial_transactions', rows: this.db.financials as unknown as Record<string, unknown>[] },
        { name: 'documents', rows: this.db.documents as unknown as Record<string, unknown>[] },
        { name: 'amendments', rows: this.db.amendments as unknown as Record<string, unknown>[] },
        { name: 'profiles', rows: this.db.users as unknown as Record<string, unknown>[] },
        { name: 'audit_logs', rows: this.db.audit as unknown as Record<string, unknown>[] },
      ]),
  };

  search: DataRepository['search'] = {
    global: async (term) => {
      const s = norm(term).trim();
      if (!s) return delay({ tenders: [], vendors: [] });
      const tenders = this.allSummaries()
        .filter((t) => [t.gemId, t.tenderNumber, t.poNumber, t.contractNumber, t.title, t.vendorName].some((v) => norm(v).includes(s)))
        .slice(0, 6)
        .map(({ id, gemId, tenderNumber, title, vendorName, status }) => ({ id, gemId, tenderNumber, title, vendorName, status }));
      const vendors = this.db.vendors
        .filter((v) => norm(v.name).includes(s) || norm(v.vendorCode).includes(s))
        .slice(0, 4)
        .map(({ id, name, vendorCode }) => ({ id, name, vendorCode }));
      return delay({ tenders, vendors });
    },
  };
}
